import React, { useMemo, useState } from "react";
import { Wrench, ChevronRight, Loader2, Camera, FileDown } from "lucide-react";
import { clientPortalApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtDate, statusTone, prettyStatus, apiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { generateMaintenanceReportForJob } from "@/pdf/webMaintenanceReport";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export default function PortalMaintenance() {
  const { data, loading, error, reload } = useAsync(() => clientPortalApi.listMaintenanceJobs(), []);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const jobs = Array.isArray(data) ? data : (data?.items || []);
  const groups = useMemo(() => {
    const m = new Map();
    for (const j of jobs) {
      const key = j.building_name || "Other";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(j);
    }
    return [...m.entries()];
  }, [jobs]);

  const open = async (job) => {
    if (job.is_quote_placeholder) { setDetail({ ...job }); return; }
    setDetailLoading(true);
    setDetail({ ...job });
    try {
      const r = await clientPortalApi.getMaintenanceJob(job.id);
      setDetail(r.data || job);
    } catch (e) { /* show summary only */ }
    finally { setDetailLoading(false); }
  };

  const downloadCompletion = async () => {
    setPdfBusy(true);
    try {
      await generateMaintenanceReportForJob(detail);
    } catch (e) {
      toast.error(apiError(e, "Could not generate the completion report"));
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Maintenance" subtitle="Remedial works on your buildings" testId="portal-maintenance-header" />
      {loading && <LoadingState label="Loading jobs…" rows={4} />}
      {error && <ErrorState onRetry={reload} />}
      {!loading && !error && (
        jobs.length === 0 ? (
          <EmptyState title="No maintenance jobs" message="Remedial works raised on your buildings will appear here." />
        ) : (
          <div className="space-y-6">
            {groups.map(([name, list]) => (
              <div key={name}>
                <h3 className="mb-2 text-sm font-semibold text-slate-500">{name}</h3>
                <div className="space-y-2">
                  {list.map((j) => (
                    <Card key={j.id} className="flex items-center justify-between gap-2 p-4 cursor-pointer hover:bg-slate-50" onClick={() => open(j)} data-testid={`portal-job-${j.id}`}>
                      <div className="min-w-0">
                        <p className="flex items-center gap-1.5 font-medium text-slate-800"><Wrench className="h-4 w-4 text-primary" /> {j.door_reference || j.title || "Remedial job"}</p>
                        <p className="mt-0.5 text-xs text-slate-400">{j.scheduled_for ? fmtDate(j.scheduled_for) : "Not scheduled"}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge className={statusTone(j.status)}>{prettyStatus(j.status)}</Badge>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="portal-job-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>{detail.door_reference || detail.title || "Remedial job"}</DialogTitle>
                <DialogDescription>{detail.building_name} · {prettyStatus(detail.status)}</DialogDescription>
              </DialogHeader>
              {detail.is_quote_placeholder ? (
                <div className="space-y-2 text-sm">
                  <p className="text-slate-600">This quote has been <strong>approved</strong> and is awaiting scheduling. We&apos;ll be in touch to book the works with an engineer.</p>
                  {detail.assigned_at && <p className="text-xs text-slate-400">Approved {fmtDate(detail.assigned_at)}</p>}
                </div>
              ) : detailLoading ? (
                <LoadingState label="Loading details…" rows={2} />
              ) : (() => {
                const c = detail.completion || {};
                const done = !!(c.work_done || (Array.isArray(c.item_completions) && c.item_completions.length) || detail.completed_at);
                return (
                  <div className="space-y-3 text-sm">
                    {c.work_done && (<div><p className="font-semibold text-slate-700">Work carried out</p><p className="whitespace-pre-line text-slate-600">{c.work_done}</p></div>)}
                    {Array.isArray(c.item_completions) && c.item_completions.length > 0 && (
                      <div><p className="font-semibold text-slate-700">Components repaired</p><ul className="list-disc pl-5 text-slate-600">{c.item_completions.map((ic, i) => <li key={i}>{ic.component}{ic.notes ? ` — ${ic.notes}` : ""}</li>)}</ul></div>
                    )}
                    {Array.isArray(c.materials) && c.materials.length > 0 && (
                      <div><p className="font-semibold text-slate-700">Materials</p><ul className="list-disc pl-5 text-slate-600">{c.materials.map((m, i) => <li key={i}>{m.name || m}</li>)}</ul></div>
                    )}
                    {Array.isArray(c.photos) && c.photos.length > 0 && (
                      <div>
                        <p className="mb-1 flex items-center gap-1 font-semibold text-slate-700"><Camera className="h-3.5 w-3.5" /> Photos</p>
                        <div className="flex flex-wrap gap-2">{c.photos.map((p, i) => <img key={i} src={p} alt="" className="h-20 w-20 rounded object-cover" />)}</div>
                      </div>
                    )}
                    {detail.post_work_inspection && (
                      <p className="rounded-lg bg-emerald-50 p-2 text-xs text-emerald-800">Re-inspected {fmtDate(detail.post_work_inspection.inspection_date)} — outcome <strong>{prettyStatus(detail.post_work_inspection.status)}</strong>.</p>
                    )}
                    {detail.completed_at && <p className="text-xs text-slate-400">Completed {fmtDate(detail.completed_at)}</p>}
                    {done ? (
                      <Button size="sm" variant="outline" onClick={downloadCompletion} disabled={pdfBusy} data-testid="portal-completion-report">
                        {pdfBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />} Download completion report
                      </Button>
                    ) : (
                      <p className="text-slate-500">No completion details yet — this job is still in progress.</p>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
